<template>
  <v-container class="class-desc-admin">
    <div class="page-header">
      <h2>IABF {{ adminFestivalYear }} — Class titles &amp; descriptions</h2>
      <div>
        <v-btn outlined :to="{ path: '/iabf/admin/itinerary' }">Back to itinerary →</v-btn>
      </div>
    </div>
    <p class="muted">All classes across the week. Tap any title or description to edit — saves automatically on blur.</p>

    <v-tabs v-model="activeDay" class="day-tabs" background-color="transparent">
      <v-tab v-for="d in daysWithClasses" :key="d.key" :href="`#${d.key}`">{{ d.label }} ({{ classesForDay(d.key).length }})</v-tab>
    </v-tabs>

    <div v-if="loading" class="muted pad-y">Loading…</div>

    <div v-else>
      <div v-for="d in daysWithClasses" :key="d.key" v-show="activeDay === d.key">
        <div class="class-list">
          <div v-for="c in classesForDay(d.key)" :key="c.id" class="class-card">
            <div class="card-meta">
              <span class="meta-time">{{ c.timeLabel || '?' }}</span>
              <span class="meta-tracks">{{ trackLabelsFor(c) }}</span>
              <span v-if="c.teacher" class="meta-teacher">{{ c.teacher }}</span>
              <span v-if="(c.room || c.defaultRoom)" class="meta-room">📍 {{ c.room || c.defaultRoom }}</span>
              <span v-if="savedFlash[c.id]" class="saved-chip">✓ saved</span>
              <span v-if="saveError[c.id]" class="error-chip">⚠ {{ saveError[c.id] }}</span>
            </div>

            <label class="field-lab">Title</label>
            <input
              class="field-input field-title"
              :value="c.title || ''"
              @blur="saveField(c, 'title', $event.target.value)"
              @keydown.enter="$event.target.blur()"
              placeholder="(untitled)"
            />

            <label class="field-lab">Description</label>
            <textarea
              class="field-input field-desc"
              :value="c.description || ''"
              @blur="saveField(c, 'description', $event.target.value)"
              rows="3"
              placeholder="Add a short description — shown on the dancer schedule when they tap Details ▼"
            ></textarea>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { db } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'


const TRACK_LABEL = {
  rehearsal:  'Rehearsal',
  begBallet:  'Beg. Ballet',
  intBallet:  'Int Ballet',
  contemp:    'Contemporary',
  all:        'All Tracks'
}

const DAY_LABEL = {
  tuesday:   'Tuesday',
  wednesday: 'Wednesday',
  thursday:  'Thursday',
  friday:    'Friday'
}

const DAY_ORDER = { tuesday: 2, wednesday: 3, thursday: 4, friday: 5 }

function parseSlotRange (label) {
  const m = String(label || '').match(/(\d{1,2})(?::(\d{2}))?\s*-\s*(\d{1,2})(?::(\d{2}))?/)
  if (!m) return { start: 0, end: 0 }
  // 12-hour labels: hours 1-6 are PM at IABF (nothing starts before 7 AM).
  const toPm = (h) => (h >= 1 && h <= 6) ? h + 12 : h
  const sh = toPm(parseInt(m[1], 10))
  const sm = m[2] ? parseInt(m[2], 10) : 0
  const eh = toPm(parseInt(m[3], 10))
  const em = m[4] ? parseInt(m[4], 10) : 0
  return { start: sh * 60 + sm, end: eh * 60 + em }
}

function parse24h (s) {
  const m = String(s || '').match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

function classSortKey (c) {
  const startCanon = parse24h(c.startTime || '')
  const endCanon = parse24h(c.endTime || '')
  if (startCanon !== null) {
    return { start: startCanon, end: endCanon !== null ? endCanon : startCanon }
  }
  const fromLabel = parseSlotRange(c.timeLabel || '')
  return { start: fromLabel.start || (c.order || 0), end: fromLabel.end || 0 }
}

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      loading: true,
      classes: [],
      activeDay: 'tuesday',
      savedFlash: {},
      saveError: {}
    }
  },
  computed: {
    daysWithClasses () {
      const seen = new Set()
      for (const c of this.classes) {
        if (DAY_LABEL[c.day]) seen.add(c.day)
      }
      const order = Object.keys(DAY_LABEL).filter(d => seen.has(d))
      return order
        .map(d => ({ key: d, label: DAY_LABEL[d], dayOrder: DAY_ORDER[d] }))
        .sort((a, b) => a.dayOrder - b.dayOrder)
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    const snap = await db.collection(this.IABF.classes).get()
    this.classes = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    this.loading = false
    if (this.daysWithClasses.length > 0) {
      this.activeDay = this.daysWithClasses[0].key
    }
  },
  methods: {
    classesForDay (day) {
      return this.classes
        .filter(c => c.day === day)
        .slice()
        .sort((a, b) => {
          const ka = classSortKey(a)
          const kb = classSortKey(b)
          if (ka.start !== kb.start) return ka.start - kb.start
          return kb.end - ka.end
        })
    },
    trackLabelsFor (c) {
      const ts = Array.isArray(c.tracks) && c.tracks.length ? c.tracks : (c.track ? [c.track] : [])
      return ts.map(t => TRACK_LABEL[t] || t).join(' · ')
    },
    async saveField (cls, field, raw) {
      const next = (raw || '').trim()
      const current = (cls[field] || '').trim()
      if (next === current) return
      this.$set(this.saveError, cls.id, '')
      try {
        await db.collection(this.IABF.classes).doc(cls.id).update({ [field]: next })
        cls[field] = next
        this.$set(this.savedFlash, cls.id, true)
        setTimeout(() => { this.$set(this.savedFlash, cls.id, false) }, 1800)
      } catch (e) {
        this.$set(this.saveError, cls.id, e.message || 'Save failed')
      }
    }
  }
}
</script>

<style scoped>
.class-desc-admin { max-width: 900px; padding-bottom: 60px; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; flex-wrap: wrap; gap: 12px; }
h2 { font-family: Quattrocento, Georgia, serif; font-weight: 400; font-size: 26px; margin: 0; color: #1a1a1a; }
.muted { color: #888; font-style: italic; font-size: 14px; }
.pad-y { padding: 30px 0; }
.day-tabs { margin: 12px 0; }

.class-list { display: flex; flex-direction: column; gap: 12px; padding: 12px 0; }
.class-card { background: #fff; border: 1px solid #ece5d2; border-radius: 6px; padding: 14px 16px; box-shadow: 0 1px 0 rgba(0,0,0,.04); }
.card-meta { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; font-family: 'Fanwood Text', Georgia, serif; font-size: 13px; color: #555; margin-bottom: 10px; }
.meta-time { font-family: Quattrocento, Georgia, serif; font-weight: 700; color: #1a1a1a; font-size: 14px; }
.meta-teacher { color: #1a4452; font-weight: 600; }
.meta-room { color: #267E7D; }
.meta-tracks { background: #f5efe0; padding: 2px 8px; border-radius: 10px; font-size: 12px; }
.saved-chip { color: #2e7d32; font-size: 12px; }
.error-chip { color: #c62828; font-size: 12px; }

.field-lab { display: block; font-family: 'Fanwood Text', Georgia, serif; font-weight: 700; font-size: 12px; color: #777; margin: 6px 0 3px; }
.field-input { width: 100%; padding: 8px 10px; border: 1px solid #d8d0bb; border-radius: 4px; font-family: 'Fanwood Text', Georgia, serif; font-size: 14px; background: #fffefb; box-sizing: border-box; }
.field-input:focus { outline: none; border-color: #267E7D; background: #fff; }
.field-title { font-family: Quattrocento, Georgia, serif; font-weight: 700; font-size: 16px; }
.field-desc { resize: vertical; min-height: 70px; }
</style>
